/**
 * Shared money formatter — the single place the currency symbol comes from.
 *
 * Replaces the per-tab `$`-hardcoded helpers (MarketSizing's fmtMoney,
 * ExecutiveSummary's fmt, StrategyRoadmap's fmtK). The currency code arrives
 * on the report payload as `_currency` (resolved by the backend from the
 * connected accounts, same delivery as `_vertical`); absent/unknown keeps the
 * historical `$` so existing USD reports render unchanged.
 */

const SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', RUB: '₽', INR: '₹',
};

function render(num: string, currency?: string | null): string {
  const code = (currency ?? '').trim().toUpperCase();
  if (!code || SYMBOLS[code]) return `${SYMBOLS[code] ?? '$'}${num}`;
  return `${code} ${num}`; // no well-known symbol → unambiguous "PLN 1.2K"
}

/** $2.6K / €241K / PLN 1.2M — K/M compaction with the project's currency.
 *  `subTenKDecimal` keeps one decimal for sub-$10K values (MarketSizing's
 *  behaviour, so a real $2,552 isn't rounded away to "$3K"). */
export function formatMoney(
  n: number,
  currency?: string | null,
  opts?: { subTenKDecimal?: boolean },
): string {
  if (n >= 1e6) return render(`${(n / 1e6).toFixed(1)}M`, currency);
  if (n >= 1e3) {
    const decimals = opts?.subTenKDecimal && n < 1e4 ? 1 : 0;
    return render(`${(n / 1e3).toFixed(decimals)}K`, currency);
  }
  return render(`${Math.round(n)}`, currency);
}
